import { cn } from "@/lib/utils";
import { Activity, Dumbbell, TrendingUp, Zap } from "lucide-react";
import { Card, CardContent } from "../ui/card";

interface WorkoutSummaryProps {
  totalSets: number;
  completedSets: number;
  totalVolume: number;
  averageRPE: number | null;
  unit?: "kg" | "lbs";
  className?: string;
}

export function WorkoutSummary({
  totalSets,
  completedSets,
  totalVolume,
  averageRPE,
  unit = "kg",
  className,
}: WorkoutSummaryProps) {
  const completionPercentage =
    totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

  const formatVolume = (volume: number): string => {
    if (unit === "kg" && volume >= 1000) {
      return `${(volume / 1000).toFixed(1)}t`;
    }
    if (unit === "lbs" && volume >= 2000) {
      return `${(volume / 2000).toFixed(1)}t`;
    }
    return `${volume.toLocaleString()} ${unit}`;
  };

  return (
    <div className={cn("grid grid-cols-2 gap-3 md:grid-cols-4", className)}>
      {/* Sets Completed */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-4">
          <div className="mb-2 flex items-center gap-2 text-muted-foreground">
            <Dumbbell className="h-4 w-4" />
            <span className="text-xs">Setler</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="font-bold text-2xl">{completedSets}</span>
            <span className="text-muted-foreground text-sm">/ {totalSets}</span>
          </div>
          {/* Progress bar */}
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-300",
                completionPercentage === 100
                  ? "bg-green-500 dark:bg-green-400"
                  : completionPercentage > 50
                    ? "bg-primary"
                    : "bg-orange-500 dark:bg-orange-400",
              )}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Total Volume */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-4">
          <div className="mb-2 flex items-center gap-2 text-muted-foreground">
            <TrendingUp className="h-4 w-4" />
            <span className="text-xs">Toplam Volüm</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="font-bold text-2xl">
              {formatVolume(totalVolume)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Average RPE */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-4">
          <div className="mb-2 flex items-center gap-2 text-muted-foreground">
            <Activity className="h-4 w-4" />
            <span className="text-xs">Ort. RPE</span>
          </div>
          <div className="flex items-baseline gap-1">
            {averageRPE !== null ? (
              <span
                className={cn(
                  "font-bold text-2xl",
                  averageRPE >= 9
                    ? "text-red-500 dark:text-red-400"
                    : averageRPE >= 7
                      ? "text-orange-500 dark:text-orange-400"
                      : "text-green-500 dark:text-green-400",
                )}
              >
                {averageRPE.toFixed(1)}
              </span>
            ) : (
              <span className="text-2xl text-muted-foreground">-</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Completion Status */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-4">
          <div className="mb-2 flex items-center gap-2 text-muted-foreground">
            <Zap className="h-4 w-4" />
            <span className="text-xs">Tamamlama</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span
              className={cn(
                "font-bold text-2xl",
                completionPercentage === 100
                  ? "text-green-500 dark:text-green-400"
                  : completionPercentage > 50
                    ? "text-primary"
                    : "text-orange-500 dark:text-orange-400",
              )}
            >
              %{completionPercentage}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Compact version for mobile
interface CompactSummaryProps {
  completedSets: number;
  totalSets: number;
  className?: string;
}

export function CompactWorkoutSummary({
  completedSets,
  totalSets,
  className,
}: CompactSummaryProps) {
  const percentage =
    totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-2",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <Dumbbell className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">
          {completedSets}/{totalSets} set
        </span>
      </div>
      <div className="h-4 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-300",
            percentage === 100
              ? "bg-green-500 dark:bg-green-400"
              : percentage > 50
                ? "bg-primary"
                : "bg-orange-500 dark:bg-orange-400",
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span
        className={cn(
          "font-medium text-sm",
          percentage === 100
            ? "text-green-500 dark:text-green-400"
            : percentage > 50
              ? "text-primary"
              : "text-orange-500 dark:text-orange-400",
        )}
      >
        %{percentage}
      </span>
    </div>
  );
}
